import React, { useRef, useState, useEffect } from 'react'
import { Mic, Square, Upload, Loader2, Trash2 } from 'lucide-react'
import { uploadSectionAudio } from '../api/lessonBuilderApi'

// Lựa chọn audio KHÔNG dùng AI (không trừ điểm), đặt cạnh nút "Tạo Audio" (TTS):
//  • 🎤 Ghi âm   — thu qua micro (MediaRecorder); dừng → NGHE NGAY (preview) + tự tải lên.
//  • ⬆️ Tải lên  — chọn file audio có sẵn.
//  • 🗑️ Xóa      — gỡ audio hiện tại của phần/khối.
// Tải lên gọi /lesson-builder/voice/upload rồi đặt audioUrl; Xóa đặt audioUrl=''.
interface Props {
  lessonId: string
  sectionId: string
  currentUrl?: string                                   // audioUrl hiện có (để hiện nút Xóa)
  onAudio: (patch: { audioUrl: string }) => void
  disabled?: boolean
  className?: string
}

const AudioSourceButtons: React.FC<Props> = ({ lessonId, sectionId, currentUrl, onAudio, disabled, className }) => {
  const [busy, setBusy] = useState(false)
  const [recording, setRecording] = useState(false)
  const [secs, setSecs] = useState(0)
  const [msg, setMsg] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)   // blob URL bản vừa thu (nghe ngay)
  const recRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const previewRef = useRef<string | null>(null)

  const setPrev = (u: string | null) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    previewRef.current = u
    setPreview(u)
  }

  useEffect(() => () => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    recRef.current?.stream?.getTracks().forEach((t) => t.stop())
    if (previewRef.current) URL.revokeObjectURL(previewRef.current)
  }, [])

  const doUpload = async (blob: Blob, filename: string) => {
    setBusy(true); setMsg(null)
    try {
      const r = await uploadSectionAudio(lessonId, sectionId, blob, filename)
      onAudio({ audioUrl: `${r.url}?t=${Date.now()}` })
      setPrev(null)   // đã có URL server (parent hiển thị) → bỏ preview tạm
      setMsg('✅ Đã thêm audio. Nhớ bấm Lưu bài để giữ lại.')
    } catch (e: any) {
      // Giữ preview để vẫn NGHE được + thử lại; báo lỗi rõ.
      setMsg(`Lỗi tải lên: ${e?.message || 'không tải được audio'} — bản thu vẫn nghe thử được bên dưới.`)
    } finally { setBusy(false) }
  }

  const startRec = async () => {
    setMsg(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      chunksRef.current = []
      mr.ondataavailable = (e) => { 
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data) 
      }
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        const type = chunksRef.current[0]?.type || mr.mimeType || 'audio/webm'
        const baseType = type.split(';')[0] // Tránh 'audio/webm;codecs=opus' gây lỗi Content-Type trên server
        const blob = new Blob(chunksRef.current, { type: baseType })
        
        if (blob.size === 0) {
          setMsg('Lỗi: File ghi âm rỗng, vui lòng thử lại.')
          return
        }

        let ext = 'webm'
        if (type.includes('mp4')) ext = 'mp4'
        else if (type.includes('ogg')) ext = 'ogg'
        else if (type.includes('mpeg')) ext = 'mp3'

        setPrev(URL.createObjectURL(blob))   // nghe ngay, không chờ server
        await doUpload(blob, `ghi-am.${ext}`)
      }
      mr.start(200) // Đảm bảo lấy được data liên tục, fix lỗi file rỗng trên một số trình duyệt
      recRef.current = mr
      setRecording(true); setSecs(0)
      timerRef.current = window.setInterval(() => setSecs((s) => s + 1), 1000)
    } catch {
      setMsg('Không truy cập được micro. Hãy cho phép quyền micro cho trang.')
    }
  }

  const stopRec = () => {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null }
    recRef.current?.stop()
    setRecording(false)
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('audio/')) { setMsg('Vui lòng chọn file âm thanh.'); return }
    setPrev(URL.createObjectURL(file))
    await doUpload(file, file.name || 'audio')
  }

  const removeAudio = () => {
    setPrev(null)
    onAudio({ audioUrl: '' })
    setMsg('Đã xóa audio. Nhớ bấm Lưu bài.')
  }

  const mm = String(Math.floor(secs / 60)).padStart(2, '0')
  const ss = String(secs % 60).padStart(2, '0')
  const hasAudio = !!(preview || currentUrl)

  return (
    <span className={`inline-flex items-center gap-1.5 flex-wrap ${className || ''}`}>
      {recording ? (
        <button type="button" onClick={stopRec}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-600 text-white text-[11px] font-bold hover:bg-red-700 animate-pulse">
          <Square size={11} /> Dừng {mm}:{ss}
        </button>
      ) : (
        <button type="button" onClick={startRec} disabled={disabled || busy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-100 text-rose-700 text-[11px] font-bold hover:bg-rose-200 disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Mic size={11} />} Ghi âm
        </button>
      )}
      <label className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-sky-100 text-sky-700 text-[11px] font-bold hover:bg-sky-200 cursor-pointer ${(disabled || busy || recording) ? 'opacity-50 pointer-events-none' : ''}`}>
        <Upload size={11} /> Tải lên
        <input type="file" accept="audio/*" className="hidden" onChange={onPickFile} disabled={disabled || busy || recording} />
      </label>
      {hasAudio && !recording && (
        <button type="button" onClick={removeAudio} disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 text-[11px] font-bold hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50">
          <Trash2 size={11} /> Xóa
        </button>
      )}
      {/* Nghe NGAY bản vừa thu/chọn (chỉ hiện khi có lỗi để tránh nháy giao diện khi tải lên thành công) */}
      {preview && !busy && msg && <audio controls src={preview} className="h-8 max-w-[220px]" />}
      {msg && <span className="text-[10px] text-gray-600 w-full">{msg}</span>}
    </span>
  )
}

export default AudioSourceButtons
